import {
  computed,
  Directive,
  input,
  output,
  OutputEmitterRef,
  inject,
  InputSignal,
  ElementRef,
  TemplateRef
} from '@angular/core';

import { from, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HrefParams } from '../../domain/href.domain';
import { KeyCode } from '../../domain/key-code.enum';
import { WINDOW_TOKEN } from '../../services/window.token';
import { LinkRegistryService } from '../../services/link-registry.service';
import { ButtonComponent } from '../../components/button/button.component';
import { DataAnalyticsService } from '../../services/data-analytics/data-analytics.service';
import { RouterBaseService, StateDeclaration } from '../../services/router/router-base.service';

import type { Observable, ObservableInput } from 'rxjs';

export type { HrefParams } from '../../domain/href.domain';

// ReferenceType distinguishes between link-based navigation and state-based navigation
enum ReferenceType {
  LINK,
  STATE
}
// Event interface for async navigation output
export interface HrefNavigationEvent {
  ref: string;
  params?: HrefParams;
  options?: unknown;
  navigate(ref?: string, params?: HrefParams, options?: unknown): Observable<void>;
}

// Internal descriptor for navigation context
interface Descriptor {
  ref: string;
  refType: ReferenceType;
  params: HrefParams;
  options: unknown;
  async: boolean;
}

/**
 * This directive's behaviour is similar to href, but it also supports state names and link ids from the link registry as a parameter.
 */

/**
 * HrefDirective
 * Provides navigation via href, state names, or link registry ids.
 * - Supports signals for href, params, options, async navigation, and confirmation templates.
 * - Emits navigation events for async mode.
 * - Handles role and target attributes for accessibility and browser behaviour.
 */
@Directive({
  selector: '[tdWbHref]',
  // Host bindings for href, role, and event handlers
  host : {
    '[attr.href]': 'hrefAttr',
    '[attr.role]': 'roleAttr',
    '(keydown)': 'onKeyDown($event)',
    '(click)': 'onClick($event)'
  }
})
export class HrefDirective {
  private static readonly CURRENT_STATE_PLACEHOLDER: string = '$current';

  //Signals for directive inputs
  href: InputSignal<string> = input('', { alias: 'tdWbHref' });
  hrefParams: InputSignal<HrefParams | undefined> = input( undefined as HrefParams | undefined, { alias: 'tdWbHrefParams' });
  hrefOptions: InputSignal<unknown> = input<unknown>(undefined, { alias: 'tdWbHrefOptions' });
  tdWbHrefTaggingEvent: InputSignal<string> = input('', { alias: 'tdWbHrefTaggingEvent' });
  tdWbHrefTaggingType: InputSignal<string> = input('link', { alias: 'tdWbHrefTaggingType' });
  confirmTemplate: InputSignal<TemplateRef<unknown> | null> = input<TemplateRef<unknown> | null>(null, { alias: 'confirmTemplate' });
  asyncNavigate: InputSignal<boolean> = input(false, { alias: 'tdWbHrefAsync' });

  readonly onNavigate: OutputEmitterRef<HrefNavigationEvent> = output<HrefNavigationEvent>();

  // Injected services for navigation, analytics, and DOM access
  private readonly linkRegistryService = inject(LinkRegistryService);
  private readonly routerService = inject(RouterBaseService);
  private readonly button: ButtonComponent | null = inject(ButtonComponent, { optional: true, host: true });
  private readonly browserWindow = inject(WINDOW_TOKEN);
  private readonly dataAnalyticsService = inject(DataAnalyticsService);
  private readonly el: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * The static `role` attribute value captured at construction time, before any host bindings run.
   * Component host bindings (e.g. `[attr.role]: '"button"'`) are applied during change detection,
   * AFTER the directive is constructed. Reading `getAttribute('role')` here therefore captures only
   * explicitly-set template attributes (e.g. `role="navigation"`), not dynamic host-binding values.
   * This prevents the computed from accidentally locking in the component's dynamic role.
   */
  private readonly initialHostRole: string | null = this.el.nativeElement.getAttribute('role');

  private readonly descriptorSignal = computed<Descriptor | undefined>(() => {
    const href: string = this.href();
    if (!href) {
      return undefined;
    }

    return this.getDescriptor(href, this.hrefParams(), this.hrefOptions(), this.confirmTemplate() != null);
  });

  private readonly hrefAttrSignal = computed<string>(() => {
    const descriptor = this.descriptorSignal();
    if (descriptor === undefined || !this.isHref(descriptor)) {
      return '';
    }

    return this.getHref(descriptor);
  });

  private readonly roleAttrSignal = computed<string | null>(() => {
    if (this.button !== null) {
      return null;
    }

    const descriptor = this.descriptorSignal();
    if (descriptor === undefined) {
      return this.initialHostRole ?? 'button';
    }

    return this.isHref(descriptor)
      ? (this.initialHostRole ?? 'link')
      : (this.initialHostRole ?? 'button');
  });

  get hrefAttr(): string {
    return this.hrefAttrSignal();
  }

  get roleAttr(): string | null {
    return this.roleAttrSignal();
  }

  private get targetAttrFromHost(): string | null {
    return this.el.nativeElement.getAttribute('target');
  }

  onKeyDown(event: KeyboardEvent): void {
    const keyCode = event.key;
    if (((this.roleAttr === 'button' || this.button !== null) && (keyCode === KeyCode.ENTER || keyCode === KeyCode.SPACE)) ||
        (this.roleAttr === 'link' && keyCode === KeyCode.ENTER)) {
      this.click(event);
    }
  }

  onClick(event: MouseEvent): void {
    this.click(event);
  }

  private click(event: UIEvent): void {
    if (this.tdWbHrefTaggingEvent() !== undefined && this.tdWbHrefTaggingEvent() !== '') {
      this.dataAnalyticsService.emitLinkClickEvent(this.tdWbHrefTaggingEvent(), this.tdWbHrefTaggingType());
    }
    event.preventDefault();
    this.navigate();
  }

  // If asyncNavigate is true, emits navigation event for external handling. Otherwise, performs navigation directly.
  private navigate(): void {
    const descriptor = this.descriptorSignal();
    if (descriptor === undefined) {
      return;
    }

    if (this.asyncNavigate()) {
      const event: HrefNavigationEvent = {
        ref: descriptor.ref,
        navigate: (ref?: string, params?: HrefParams, options?: unknown): Observable<void> => {
          const nextDescriptor = ref === undefined
            ? this.descriptorSignal()
            : this.getDescriptor(ref, params, options, this.asyncNavigate());

          if (nextDescriptor === undefined) {
            return of();
          }

          return this.doNavigate(nextDescriptor);
        }
      };
      if (descriptor.params !== undefined) {
        event.params = descriptor.params;
      }
      if (descriptor.options !== undefined) {
        event.options = descriptor.options;
      }
      this.onNavigate.emit(event);
    } else {
      this.doNavigate(descriptor);
    }
  }

  // For LINK type, resolves href and navigates via window.location or window.open. For STATE type, navigated via routerService.go
  private doNavigate(descriptor: Descriptor): Observable<void> {
    if (descriptor.refType === ReferenceType.LINK) {
      const href = this.getHref(descriptor);
      if (this.targetAttrFromHost === null) {
        // Based on existence of href strategy we will know who to call
        this.browserWindow.location.assign(href);
      } else {
        this.browserWindow.open(href, this.targetAttrFromHost);
      }
      return of();
    } else {
      const stateName = descriptor.ref === HrefDirective.CURRENT_STATE_PLACEHOLDER ? this.routerService.getGlobalsCurrent() : descriptor.ref;
      return from(this.routerService.go(stateName, descriptor.params, descriptor.options) as ObservableInput<unknown>).pipe(map((): void => {
      }));
    }
  }

  //Determines navigation type (LINK or STATE) based on ref and registry lookups.
  private getDescriptor(ref: string, params?: HrefParams, options?: unknown, confirm?: boolean): Descriptor {
    const result = {
      ref,
      params,
      options,
      async: confirm === true,
      refType: ReferenceType.LINK
    } as Descriptor;
    let stateDefinition: StateDeclaration;

    if (result.ref === HrefDirective.CURRENT_STATE_PLACEHOLDER) {
      result.refType = ReferenceType.STATE;
    } else if (this.linkRegistryService.getLink(ref, undefined, true) === undefined) {
      stateDefinition = this.routerService.getStateRegistry(ref);
      if ((stateDefinition !== null && stateDefinition)) {
        result.refType = ReferenceType.STATE;
      }
    }
    return result;
  }

  private isHref(descriptor: Descriptor): boolean {
    return descriptor.refType === ReferenceType.LINK && !descriptor.async;
  }

  private getHref(descriptor: Descriptor): string {
    let href = this.linkRegistryService.getLink(descriptor.ref, descriptor.params, true);
    if (href === undefined) {
      if (descriptor.params !== undefined) {
        href = this.linkRegistryService.addParameters(descriptor.ref, descriptor.params);
      } else {
        href = descriptor.ref;
      }
    }
    return href;
  }
}
